import React from 'react';

import orderBy from 'lodash.orderby';
import { Building2 } from 'lucide-react';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { AsyncQuickAddSection } from '@/_containers';
import type { CompanyQueryLinkerWithPivotSchema } from '@/_schemas/companies/company-query-linker-with-pivot-schema';

import { CompanyItem } from './item';

import { useGetCompaniesMutation } from '../../apis';

type CompanyMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<CompanyQueryLinkerWithPivotSchema, TFormValues>,
  | 'isEditable'
  | 'control'
  | 'fieldName'
  | 'title'
  | 'sectionTitle'
  | 'icon'
  | 'apiParams'
  | 'paginatedItems'
  | 'onValuesChange'
> & {
  companyId?: string | number;
  companies: CompanyQueryLinkerWithPivotSchema[];
  showHeader?: boolean;
  canSetPrimary?: boolean;
  /** Primary (starred) items are always read-only, regardless of `isEditable`. */
  primaryReadOnly?: boolean;
  renderItem?: QuickItemRenderer<CompanyQueryLinkerWithPivotSchema>;
};

export function CompanyMultiSelect<TFormValues extends FieldValues = FieldValues>({
  companyId,
  title = 'Companies',
  sectionTitle = 'Companies',
  icon = Building2,
  companies = [],
  canSetPrimary = true,
  primaryReadOnly = false,
  renderItem,
  ...rest
}: CompanyMultiSelectProps<TFormValues>) {
  const renderClient: QuickItemRenderer<CompanyQueryLinkerWithPivotSchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <CompanyItem
        key={item.id}
        canSetPrimary={canSetPrimary}
        company={item}
        isEditable={isEditable && !(primaryReadOnly && item.pivot?.type === 'primary')}
        removeItemHandler={removeItem}
      />
    ),
    [canSetPrimary, primaryReadOnly]
  );
  const itemToString = React.useCallback((item: CompanyQueryLinkerWithPivotSchema) => item.name, []);
  const keyExtractor = React.useCallback((item: CompanyQueryLinkerWithPivotSchema) => item.id.toString(), []);
  const pinPrimaryFirst = React.useCallback(
    (values: CompanyQueryLinkerWithPivotSchema[]) => {
      const primaryCompanies = orderBy(
        values.filter((company) => company.pivot?.type === 'primary'),
        itemToString
      );
      const rest = orderBy(
        values.filter((company) => company.pivot?.type !== 'primary'),
        itemToString
      );
      return [...primaryCompanies, ...rest];
    },
    [itemToString]
  );
  const filterOptions = React.useCallback(
    (item: CompanyQueryLinkerWithPivotSchema) => (companyId ? item.id === Number(companyId) : false),
    [companyId]
  );

  const sortedCompanies = React.useMemo(() => pinPrimaryFirst(companies), [companies, pinPrimaryFirst]);

  return (
    <AsyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      icon={icon}
      emptyDataMessage="No Companies"
      items={sortedCompanies}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem ?? renderClient}
      useApi={useGetCompaniesMutation}
      sortValues={pinPrimaryFirst}
      filterOptions={filterOptions}
    />
  );
}
